import { apiService } from '../ApiService';
import { ClassificationsResponse, ExpertiseAreasResponse, GroupsResponse, InspectorsResponse, SitesResponse, InspectionMethodsResponse } from '@/types/response/lookupResponses';

import _ from 'lodash';
import { UserPreferencesResponse } from '@/types/response/userResponses';
import { ContractDetailsResponse, FileResponse } from '@/types/response/InspectionResultResponses';

interface LookupCache {
    loading: boolean;
    loaded: boolean;
    inspectors: Map<number, InspectorsResponse> | null;
    groups: Map<number, GroupsResponse> | null;
    sites: Map<number, SitesResponse> | null;
    expertiseAreas: Map<number, ExpertiseAreasResponse> | null;
    classifications: Map<number, ClassificationsResponse> | null;
    inspectionMethods: Map<number, InspectionMethodsResponse> | null;
    userPreferences: UserPreferencesResponse | null;
    existingResources: FileResponse[] | null;
    contractDetails: ContractDetailsResponse | null;
}

interface SerializableLookupCache {
    loading: boolean;
    loaded: boolean;
    inspectors: Array<[number, InspectorsResponse]> | null;
    groups: Array<[number, GroupsResponse]> | null;
    sites: Array<[number, SitesResponse]> | null;
    expertiseAreas: Array<[number, ExpertiseAreasResponse]> | null;
    classifications: Array<[number, ClassificationsResponse]> | null;
    inspectionMethods: Array<[number, InspectionMethodsResponse]> | null;
    userPreferences: UserPreferencesResponse | null;
    existingResources: FileResponse[] | null;
    contractDetails: ContractDetailsResponse | null;
}

export interface CAStatus {
    Id: number;
    Name: string;
}

export interface CAValidationStatus {
    Id: number;        // 0 = not set, 1 = Valid, 2 = Not Valid
    Name: string;
}

export const CA_VALIDATION_STATUSES: CAValidationStatus[] = [
    { Id: 0, Name: '' },
    { Id: 1, Name: 'Valid' },
    { Id: 2, Name: 'Not Valid' },
];

/** Maps the raw isValid boolean (or null) to a CAValidationStatus Id */
export const isValidToStatusId = (isValid: boolean | null | undefined): number => {
    if (isValid === true) return 1;
    if (isValid === false) return 2;
    return 0;
};

const CORRECTIVE_ACTION_STATUSES: CAStatus[] = [
    { Id: 0, Name: 'Issue In Progress' },
    { Id: 1, Name: 'Response Pending' },
    { Id: 2, Name: 'Acceptance Pending' },
    { Id: 3, Name: 'Accepted Open' },
    { Id: 4, Name: 'Accepted Closed' },
    { Id: 5, Name: 'Response in Progress' },
    { Id: 6, Name: 'Rescinded Closed' },
];

class LookupDataService {
    private readonly STORAGE_KEY = 'seesor.lookupCache.v1';

    private createEmptyCache(): LookupCache {
        return {
            loading: false,
            loaded: false,
            inspectors: null,
            groups: null,
            sites: null,
            expertiseAreas: null,
            classifications: null,
            inspectionMethods: null,
            userPreferences: null,
            existingResources: null,
            contractDetails: null,
        };
    }

    private cache: LookupCache = this.createEmptyCache();

    private isSessionStorageAvailable(): boolean {
        try {
            const testKey = '__lookup_cache_test__';
            sessionStorage.setItem(testKey, '1');
            sessionStorage.removeItem(testKey);
            return true;
        } catch {
            return false;
        }
    }

    private toSerializableLookup<T extends { Id: number }>(data: Map<number, T> | null): Array<[number, T]> | null {
        if (!data) return null;
        return Array.from(data.entries());
    }

    private fromSerializableLookup<T extends { Id: number }>(data: Array<[number, T]> | null): Map<number, T> | null {
        if (!data) return null;
        return new Map(data);
    }

    private persistToSessionStorage(): void {
        if (!this.isSessionStorageAvailable()) return;

        const snapshot: SerializableLookupCache = {
            loading: false,
            loaded: true,
            inspectors: this.toSerializableLookup(this.cache.inspectors),
            groups: this.toSerializableLookup(this.cache.groups),
            sites: this.toSerializableLookup(this.cache.sites),
            expertiseAreas: this.toSerializableLookup(this.cache.expertiseAreas),
            classifications: this.toSerializableLookup(this.cache.classifications),
            inspectionMethods: this.toSerializableLookup(this.cache.inspectionMethods),
            userPreferences: this.cache.userPreferences,
            existingResources: this.cache.existingResources,
            contractDetails: this.cache.contractDetails,
        };

        sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(snapshot));
    }

    private restoreFromSessionStorage(): LookupCache | null {
        if (!this.isSessionStorageAvailable()) return null;

        const raw = sessionStorage.getItem(this.STORAGE_KEY);
        if (!raw) return null;

        try {
            const parsed = JSON.parse(raw) as Partial<SerializableLookupCache>;
            if (!parsed) return null;

            return {
                loading: false,
                loaded: true,
                inspectors: this.fromSerializableLookup(parsed.inspectors ?? null),
                groups: this.fromSerializableLookup(parsed.groups ?? null),
                sites: this.fromSerializableLookup(parsed.sites ?? null),
                expertiseAreas: this.fromSerializableLookup(parsed.expertiseAreas ?? null),
                classifications: this.fromSerializableLookup(parsed.classifications ?? null),
                inspectionMethods: this.fromSerializableLookup(parsed.inspectionMethods ?? null),
                userPreferences: parsed.userPreferences ?? null,
                existingResources: parsed.existingResources ?? null,
                contractDetails: parsed.contractDetails ?? null,
            };
        } catch (error) {
            console.warn('Failed to restore lookup cache from sessionStorage:', error);
            sessionStorage.removeItem(this.STORAGE_KEY);
            return null;
        }
    }

    private async loadAllData() {
        this.cache.loading = true;
        this.cache.loaded = false;

        try {
            const [
                groups,
                sites,
                expertiseAreas,
                inspectors,
                userPreferences,
                existingResources,
                classifications,
                contractDetails,
                inspectionMethods,
            ] = await Promise.all([
                apiService.getContractGroups(),
                apiService.getContractSites(),
                apiService.getContractAreasOfExpertise(),
                apiService.getContractInspectors(),
                apiService.getContractUserPreferences(),
                apiService.getContractFiles(),
                apiService.getContractClassifications(),
                apiService.getContractDetails(),
                apiService.getInspectionMethods(),
            ]);

            this.cache.groups = this.getLookupMap(groups);
            this.cache.sites = this.getLookupMap(sites);
            this.cache.expertiseAreas = this.getLookupMap(expertiseAreas);
            this.cache.inspectors = this.getLookupMap(inspectors);
            this.cache.userPreferences = userPreferences;
            this.cache.existingResources = existingResources;
            this.cache.classifications = this.getLookupMap(classifications);
            this.cache.contractDetails = contractDetails;
            this.cache.inspectionMethods = this.getLookupMap(inspectionMethods);
            this.cache.loaded = true;
            this.persistToSessionStorage();
        } catch (error) {
            console.error('Failed to load lookup data:', error);
            this.cache = this.createEmptyCache();
            sessionStorage.removeItem(this.STORAGE_KEY);
            throw error;
        } finally {
            this.cache.loading = false;
        }
    }

    private getLookupMap<T extends { Id: number }>(data: T[] | null): Map<number, T> | null {
        if (!data) return null;
        return new Map(data.map(item => [item.Id, item]));
    }

    public isLoaded(): boolean {
        return this.cache.loaded && !this.cache.loading;
    }

    public async initialize() {
        try {
            if (this.cache.loading) {
                while (this.cache.loading) {
                    await new Promise(resolve => setTimeout(resolve, 25));
                }
                return;
            }

            if (this.cache.loaded) {
                return;
            }

            const restored = this.restoreFromSessionStorage();
            if (restored) {
                this.cache = restored;
                return;
            }

            await this.loadAllData();
        } catch (error) {
            console.error('Failed to load lookup data:', error);
            this.cache.loading = false;
            throw error;
        }
    }

    public async reload() {
        this.cache = this.createEmptyCache();
        if (this.isSessionStorageAvailable()) {
            sessionStorage.removeItem(this.STORAGE_KEY);
        }
        await this.loadAllData();
    }

    public async refreshGroups() {
        try {
            const groups = await apiService.getContractGroups();
            this.cache.groups = this.getLookupMap(groups);
        } catch (error) {
            console.error('Failed to refresh groups lookup data:', error);
            throw error;
        }
    }

    public getGroups(): Map<number, GroupsResponse> | null {
        return this.cache.groups;
    }

    public getSites(): Map<number, SitesResponse> | null {
        return this.cache.sites;
    }

    public getExpertiseAreas(): Map<number, ExpertiseAreasResponse> | null {
        return this.cache.expertiseAreas;
    }

    public getInspectors(): Map<number, InspectorsResponse> | null {
        return this.cache.inspectors;
    }

    public getUserPreferences(): UserPreferencesResponse | null {
        return this.cache.userPreferences;
    }

    public getExistingResources(): FileResponse[] | null {
        return this.cache.existingResources;
    }

    public getClassifications(): Map<number, ClassificationsResponse> | null {
        return this.cache.classifications;
    }

    public getContractDetails(): ContractDetailsResponse | null {
        return this.cache.contractDetails;
    }

    public getCorrectiveActionStatuses(): CAStatus[] {
        return CORRECTIVE_ACTION_STATUSES;
    }

    public getCAValidationStatuses(): CAValidationStatus[] {
        return CA_VALIDATION_STATUSES;
    }

    public getInspectorNameById(id: number): string {
        const inspector = this.cache.inspectors?.get(id);
        return inspector ? inspector.Name : 'Unknown Inspector';
    }

    public getGroupNameById(id: number): string {
        const group = this.cache.groups?.get(id);
        return group ? group.Name : 'Unknown Group';
    }

    public getSiteNameById(id: number): string {
        const site = this.cache.sites?.get(id);
        return site ? site.Name : 'Unknown Site';
    }
    public getInspectionMethodNameById(id: number): string {
        const method = this.cache.inspectionMethods?.get(id);
        return method ? method.Name : 'Unknown Method';
    }
    public getExpertiseAreaNameById(id: number): string {
        const area = this.cache.expertiseAreas?.get(id);
        return area ? area.Name : 'Unknown Expertise Area';
    }
}

export const lookupDataService = new LookupDataService();
export default lookupDataService;